import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Minus, Plus, ArrowCounterClockwise, CalendarCheck } from "@phosphor-icons/react";
import { openBooking } from "@/lib/booking";

const MODES = [
  ["curbside", "Curbside", "Drive-up spot: driveway, garage or alley"],
  ["full", "Full-Service", "We carry it out from inside"],
];

const Stepper = ({ id, qty, onChange }) => (
  <div className="flex items-center gap-1.5 shrink-0">
    <button type="button" data-testid={`calc-minus-${id}`} aria-label="Remove one" disabled={qty === 0} onClick={() => onChange(qty - 1)}
      className="w-9 h-9 sm:w-7 sm:h-7 rounded-full flex items-center justify-center bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"><Minus size={13} weight="bold" /></button>
    <span data-testid={`calc-qty-${id}`} className="w-5 text-center font-bold text-sm">{qty}</span>
    <button type="button" data-testid={`calc-plus-${id}`} aria-label="Add one" disabled={qty >= 10} onClick={() => onChange(qty + 1)}
      className="w-9 h-9 sm:w-7 sm:h-7 rounded-full flex items-center justify-center bg-[#46A01E] text-white disabled:opacity-40 hover:bg-[#3A8418] transition-colors"><Plus size={13} weight="bold" /></button>
  </div>
);

export default function ItemCalculator() {
  const [cats, setCats] = useState([]);
  const [minimum, setMinimum] = useState({ curbside: 130, full: 185 });
  const [mode, setMode] = useState("curbside");
  const [tab, setTab] = useState(0);
  const [qty, setQty] = useState({});

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`).then(({ data }) => {
      setCats((data.calcItems || []).filter((c) => c.items?.length));
      const t = data.tiers?.[0]?.price;
      if (t) setMinimum({ full: t, curbside: Math.round(t * (1 - (data.curbsideDiscount ?? 30) / 100)) });
    }).catch(() => {});
  }, []);

  const chosen = useMemo(() => cats.flatMap((c, ci) => c.items.map((i, ii) => ({ i, q: qty[`${ci}:${ii}`] || 0 }))
    .filter(({ q }) => q > 0).map(({ i, q }) => ({ name: i.name, qty: q, price: i[mode] }))), [cats, qty, mode]);
  const count = chosen.reduce((s, i) => s + i.qty, 0);
  const sum = chosen.reduce((s, i) => s + i.qty * i.price, 0);
  const total = count ? Math.max(sum, minimum[mode]) : 0;
  const cat = cats[tab];

  const book = () => openBooking({ booking_type: "items", service_type: mode, items: chosen, estimate: total });

  return (
    <div data-testid="item-calculator" className="rounded-[20px] text-white p-5 lg:p-6 flex flex-col h-full" style={{ background: "linear-gradient(135deg, #0f172a 0%, #15306E 100%)" }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[#5DB82F] text-xs font-bold uppercase tracking-widest">Price by the item</p>
          <h3 className="font-display font-black text-2xl tracking-tight mt-1">Just a few things?</h3>
          <p className="text-white/60 text-sm mt-1">Tap your items to see your price.</p>
        </div>
        {count > 0 && (
          <button type="button" data-testid="calc-reset" onClick={() => setQty({})} className="flex items-center gap-1 text-xs text-white/60 hover:text-white border border-white/15 rounded-full px-3 py-1.5">
            <ArrowCounterClockwise size={12} weight="bold" /> Reset
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4" data-testid="calc-mode-toggle">
        {MODES.map(([k, label, hint]) => (
          <button key={k} type="button" data-testid={`calc-mode-${k}`} onClick={() => setMode(k)}
            className={`text-left rounded-xl px-3 py-2.5 border-2 transition-colors ${mode === k ? "border-[#46A01E] bg-[#46A01E]/15" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
            <span className="block font-bold text-sm">{label}</span>
            <span className="block text-[11px] text-white/55 leading-snug mt-0.5">{hint}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4" role="tablist">
        {cats.map((c, i) => (
          <button key={c.category} type="button" role="tab" aria-selected={tab === i} data-testid={`calc-tab-${i}`} onClick={() => setTab(i)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${tab === i ? "bg-white text-[#15306E]" : "bg-white/10 text-white/75 hover:bg-white/20"}`}>{c.category}</button>
        ))}
      </div>

      <div className="mt-3 flex-1 relative min-h-[260px]">
      <ul className="space-y-1.5 overflow-y-auto pr-1 max-h-[440px] lg:max-h-none lg:absolute lg:inset-0" data-testid="calc-item-list">
        {cat?.items.map((it, i) => {
          const k = `${tab}:${i}`;
          const q = qty[k] || 0;
          return (
            <li key={k} data-testid={`calc-item-${tab}-${i}`} className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 border transition-colors ${q ? "border-[#46A01E] bg-[#46A01E]/10" : "border-white/10 bg-white/[0.03]"}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{it.name}</p>
                <p className="text-xs text-white/55">${it[mode]} each</p>
              </div>
              <Stepper id={`${tab}-${i}`} qty={q} onChange={(n) => setQty((p) => ({ ...p, [k]: Math.max(0, Math.min(10, n)) }))} />
            </li>
          );
        })}
      </ul>
      </div>

      <div className="mt-4 rounded-xl bg-[#0f172a] border border-white/10 p-4" data-testid="calc-summary">
        {count === 0 ? (
          <p className="text-center text-sm text-white/60 py-2">Add an item above to see your price.</p>
        ) : (
          <>
            <div className="flex items-end justify-between gap-3">
              <div className="text-sm text-white/70"><span data-testid="calc-count">{count}</span> item{count > 1 ? "s" : ""} · {mode === "curbside" ? "Curbside" : "Full-Service"}</div>
              <div data-testid="calc-total" className="font-display font-black text-4xl text-[#5DB82F] leading-none">${total}</div>
            </div>
            {sum < minimum[mode] && <p data-testid="calc-minimum-note" className="text-xs text-white/55 mt-2">Includes our ${minimum[mode]} minimum pickup.</p>}
            <button type="button" data-testid="calc-book-button" onClick={book}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm py-3.5 rounded-lg transition-colors">
              <CalendarCheck size={18} weight="fill" /> Book these items
            </button>
          </>
        )}
        <p className="text-[11px] text-white/40 mt-3 leading-snug">Price is based on the items you pick. Oversized, very heavy or unusual items are confirmed on-site before we start.</p>
      </div>
    </div>
  );
}
